/**
 * CartService – manages the shopping cart backed by the Cart API.
 *
 * Provides add, update, remove, clear operations plus commission handling
 * and a recent-items list for Quick-Re-Add. All mutations are persisted
 * to the remote Cart API and fire a `cart-changed` event on the global
 * EventBus so the UI (badge, page) can react in real time.
 *
 * Flow:
 * 1. On `loadCart(customerId)` the service fetches the active cart from the API
 *    (or creates one if none exists).
 * 2. Mutations update the local state immediately (optimistic UI) and then
 *    sync to the API via PUT.
 * 3. If the API call fails, the local state is rolled back and an error is thrown.
 *
 * Validates: Requirements 26.1, 26.2, 28.1–28.3, 29.1–29.3, 30.1–30.3, 31.1, 31.2
 */

import type { CartItem } from '../../shared/models/cart-item.model';
import { ApiClient } from '../../shared/services/api-client';
import { eventBus } from '../../infrastructure/event-bus';

// ---------------------------------------------------------------------------
// Cart API Client
// ---------------------------------------------------------------------------

function getCartApiBaseUrl(): string {
  try {
    return (import.meta.env?.VITE_CART_API_BASE_URL as string | undefined) ?? '';
  } catch {
    return '';
  }
}

/** Dedicated ApiClient instance for the Cart API. */
const cartApiClient = new ApiClient(getCartApiBaseUrl());

// ---------------------------------------------------------------------------
// API Types
// ---------------------------------------------------------------------------

/** Shape of a single item as stored in the Cart API. */
export interface CartApiItem {
  articleId: string;
  articleNumber: string;
  sku?: string;
  name: string;
  quantity: number;
  unit?: string;
  unitPrice?: number;
  totalPrice?: number;
  taxRate?: number;
  discount?: {
    type: 'percentage' | 'absolute';
    value: number;
    reason?: string;
  };
  note?: string;
  supplierArticleNumber: string;
  ean: string;
  packagingUnit: string;
  measurementUnit: string;
  selectedUnitName: string;
  selectedUnitQuantity: number;
  baseUnit: string;
  packageable?: boolean;
  searchQuery?: string;
  imageUrl?: string;
}

/** Shape of the Cart API response (GET/POST/PUT). */
export interface CartApiResponse {
  id: string;
  createdAt: string;
  updatedAt: string;
  customerId: string;
  status: 'active' | 'checkout' | 'completed' | 'abandoned';
  currency: string;
  items: CartApiItem[];
  couponCode?: string;
  subtotal: number;
  taxTotal: number;
  grandTotal: number;
  shippingAddress?: {
    company?: string;
    firstName?: string;
    lastName?: string;
    street?: string;
    addressLine2?: string;
    zip?: string;
    city?: string;
    country?: string;
  };
  note?: string;
  commission?: string;
  customerOrderId?: string;
  reqDeliveryDate?: string;
  selectedShippingAddressId?: string | null;
}

/** Shape of the Cart API request body (POST/PUT). */
export interface CartApiRequest {
  customerId: string;
  status: 'active' | 'checkout' | 'completed' | 'abandoned';
  currency?: string;
  items: CartApiItem[];
  couponCode?: string;
  subtotal?: number;
  taxTotal?: number;
  grandTotal?: number;
  shippingAddress?: CartApiResponse['shippingAddress'];
  note?: string;
  commission?: string;
  customerOrderId?: string;
  reqDeliveryDate?: string;
  selectedShippingAddressId?: string | null;
}

// ---------------------------------------------------------------------------
// Local Types
// ---------------------------------------------------------------------------

/** The fields the caller supplies – `id` and `addedAt` are generated. */
export type NewCartItem = Omit<CartItem, 'id' | 'addedAt'>;

/** Callback signature for cart-change listeners. */
export type CartChangedCallback = (items: CartItem[]) => void;

// ---------------------------------------------------------------------------
// Mapping helpers
// ---------------------------------------------------------------------------

/** Returns today's date as a local `YYYY-MM-DD` string. */
function getTodayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Convert a local CartItem to the API item format. */
function toApiItem(item: CartItem): CartApiItem {
  return {
    articleId: item.id,
    articleNumber: item.articleNumber,
    name: item.name,
    quantity: item.quantity,
    supplierArticleNumber: item.supplierArticleNumber,
    ean: item.ean,
    packagingUnit: item.packagingUnit,
    measurementUnit: item.measurementUnit,
    selectedUnitName: item.selectedUnitName,
    selectedUnitQuantity: item.selectedUnitQuantity,
    baseUnit: item.baseUnit,
    packageable: item.packageable,
    searchQuery: item.searchQuery,
    imageUrl: item.imageUrl,
  };
}

/** Convert an API item to a local CartItem. */
function fromApiItem(apiItem: CartApiItem, cartUpdatedAt: string): CartItem {
  return {
    id: apiItem.articleId,
    articleNumber: apiItem.articleNumber,
    name: apiItem.name ?? '',
    quantity: apiItem.quantity,
    supplierArticleNumber: apiItem.supplierArticleNumber ?? '',
    ean: apiItem.ean ?? '',
    packagingUnit: apiItem.packagingUnit ?? '',
    measurementUnit: apiItem.measurementUnit ?? '',
    selectedUnitName: apiItem.selectedUnitName ?? '',
    selectedUnitQuantity: apiItem.selectedUnitQuantity ?? 0,
    baseUnit: apiItem.baseUnit ?? '',
    packageable: apiItem.packageable,
    searchQuery: apiItem.searchQuery,
    imageUrl: apiItem.imageUrl,
    addedAt: new Date(cartUpdatedAt),
  };
}

// ---------------------------------------------------------------------------
// CartService class
// ---------------------------------------------------------------------------

export class CartService {
  private items: CartItem[] = [];
  private commission = '';
  private orderNumber = '';
  private desiredDeliveryDate = '';
  private customerId = '';
  private cartId: string | null = null;
  private selectedShippingAddressId: string | null = null;
  private listeners: CartChangedCallback[] = [];
  private loading = false;

  // -- initialization -------------------------------------------------------

  /**
   * Load the active cart for a customer from the API.
   * If no active cart exists, creates a new one.
   *
   * Each customerId gets its own separate cart. When an employee/admin
   * switches between customers, this method loads (or creates) the cart
   * specific to that customer.
   *
   * Call this after login when the customerId is known, or when the
   * selected customer changes.
   */
  async loadCart(customerId: string): Promise<void> {
    this.customerId = customerId;
    this.loading = true;

    try {
      // Fetch carts filtered by customerId so each customer has a separate cart
      const carts = await cartApiClient.get<CartApiResponse[]>(
        `/api/cart?customerId=${encodeURIComponent(customerId)}&status=active`,
      );

      // Find an active cart for this specific customer
      const activeCart = carts.find((c) => c.status === 'active' && c.customerId === customerId);

      if (activeCart) {
        this.applyApiResponse(activeCart);
      } else if (carts.length > 0) {
        // Use the most recent cart for this customer if no active one
        this.applyApiResponse(carts[0]);
      } else {
        // No cart exists for this customer – create one
        const newCart = await cartApiClient.post<CartApiResponse>('/api/cart', {
          customerId,
          status: 'active',
          currency: 'EUR',
          items: [],
          subtotal: 0,
          taxTotal: 0,
          grandTotal: 0,
        } satisfies CartApiRequest);
        this.applyApiResponse(newCart);
      }
    } finally {
      this.loading = false;
    }

    this.notifyChange();
  }

  /** Whether the service is currently loading from the API. */
  isLoading(): boolean {
    return this.loading;
  }

  /** The remote cart ID (null if not yet loaded). */
  getCartId(): string | null {
    return this.cartId;
  }

  // -- query ----------------------------------------------------------------

  /** Returns a shallow copy of the current cart items. */
  getItems(): CartItem[] {
    return [...this.items];
  }

  /** Total number of items (not sum of quantities). */
  getTotalItemCount(): number {
    return this.items.length;
  }

  /** Current commission / cost-centre text. */
  getCommission(): string {
    return this.commission;
  }

  /**
   * Set the commission / cost-centre text.
   * Persists to the API immediately.
   */
  async setCommission(commission: string): Promise<void> {
    const previous = this.commission;
    this.commission = commission;

    try {
      await this.syncToApi();
    } catch (error) {
      this.commission = previous;
      throw error;
    }
  }

  /** Current order number text. */
  getOrderNumber(): string {
    return this.orderNumber;
  }

  /**
   * Set the order number text.
   * Persists to the API immediately.
   */
  async setOrderNumber(orderNumber: string): Promise<void> {
    const previous = this.orderNumber;
    this.orderNumber = orderNumber;

    try {
      await this.syncToApi();
    } catch (error) {
      this.orderNumber = previous;
      throw error;
    }
  }

  /** Current desired delivery date (ISO date string or empty). */
  getDesiredDeliveryDate(): string {
    return this.desiredDeliveryDate;
  }

  /**
   * Set the desired delivery date.
   * Persists to the API immediately.
   *
   * @param date ISO date string (YYYY-MM-DD) or empty string to clear.
   */
  async setDesiredDeliveryDate(date: string): Promise<void> {
    const previous = this.desiredDeliveryDate;
    this.desiredDeliveryDate = date;

    try {
      await this.syncToApi();
    } catch (error) {
      this.desiredDeliveryDate = previous;
      throw error;
    }
  }

  /** Get the selected shipping address ID (from the Cart API). */
  getSelectedShippingAddressId(): string | null {
    return this.selectedShippingAddressId;
  }

  /**
   * Set the selected shipping address ID.
   * Persists to the API immediately.
   */
  async setSelectedShippingAddressId(addressId: string | null): Promise<void> {
    const previous = this.selectedShippingAddressId;
    this.selectedShippingAddressId = addressId;
    this.notifyChange();

    try {
      await this.syncToApi();
    } catch (error) {
      this.selectedShippingAddressId = previous;
      this.notifyChange();
      throw error;
    }
  }

  /**
   * Update the customerId on the cart (e.g. when an employee changes it).
   * Persists to the API immediately.
   */
  async setCustomerId(customerId: string): Promise<void> {
    const previous = this.customerId;
    this.customerId = customerId;

    try {
      await this.syncToApi();
    } catch (error) {
      this.customerId = previous;
      throw error;
    }
  }

  /**
   * Returns the most recently added items, sorted by `addedAt` descending.
   *
   * @param limit Maximum number of items to return (default 5).
   */
  getRecentItems(limit = 5): CartItem[] {
    return [...this.items]
      .sort((a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime())
      .slice(0, limit);
  }

  // -- mutations ------------------------------------------------------------

  /**
   * Add an article to the cart.
   *
   * Generates a UUID (`crypto.randomUUID()`) and sets `addedAt` to now.
   * Persists to the API and fires `cart-changed` event.
   */
  async addItem(item: NewCartItem): Promise<CartItem> {
    const cartItem: CartItem = {
      ...item,
      id: crypto.randomUUID(),
      addedAt: new Date(),
    };

    this.items.push(cartItem);
    this.notifyChange();

    try {
      await this.syncToApi();
    } catch (error) {
      // Rollback
      this.items = this.items.filter((i) => i.id !== cartItem.id);
      this.notifyChange();
      throw error;
    }

    return cartItem;
  }

  /**
   * Add multiple articles to the cart in a single operation.
   *
   * Unlike calling {@link addItem} in a loop, this pushes all items locally,
   * then performs a single API sync and fires a single `cart-changed` event.
   * This avoids the quadratic re-render / re-fetch cascade that would occur
   * when listeners (e.g. the cart page) react to each individual add.
   *
   * On API failure the entire batch is rolled back.
   *
   * @returns The created cart items.
   */
  async addItems(items: NewCartItem[]): Promise<CartItem[]> {
    if (items.length === 0) return [];

    const cartItems: CartItem[] = items.map((item) => ({
      ...item,
      id: crypto.randomUUID(),
      addedAt: new Date(),
    }));

    this.items.push(...cartItems);
    this.notifyChange();

    try {
      await this.syncToApi();
    } catch (error) {
      // Rollback the whole batch
      const addedIds = new Set(cartItems.map((i) => i.id));
      this.items = this.items.filter((i) => !addedIds.has(i.id));
      this.notifyChange();
      throw error;
    }

    return cartItems;
  }

  /**
   * Update the quantity of an existing cart item.
   *
   * Clamps to a minimum of 1. Persists to the API and fires `cart-changed` event.
   */
  async updateQuantity(itemId: string, quantity: number): Promise<void> {
    const item = this.items.find((i) => i.id === itemId);
    if (!item) return;

    const previousQuantity = item.quantity;
    item.quantity = Math.max(1, Math.floor(quantity));
    this.notifyChange();

    try {
      await this.syncToApi();
    } catch (error) {
      item.quantity = previousQuantity;
      this.notifyChange();
      throw error;
    }
  }

  /**
   * Remove a single item from the cart by its id.
   * Persists to the API and fires `cart-changed` event.
   */
  async removeItem(itemId: string): Promise<void> {
    const index = this.items.findIndex((i) => i.id === itemId);
    if (index === -1) return;

    const removed = this.items.splice(index, 1)[0];
    this.notifyChange();

    try {
      await this.syncToApi();
    } catch (error) {
      // Rollback – re-insert at original position
      this.items.splice(index, 0, removed);
      this.notifyChange();
      throw error;
    }
  }

  /**
   * Remove all items from the cart.
   * Also resets commission and order number, resets the desired delivery
   * date to today, and clears the shipping address so the cart is fully
   * clean for the next order.
   * Persists to the API and fires `cart-changed` event.
   */
  async clear(): Promise<void> {
    const previousItems = [...this.items];
    const previousAddressId = this.selectedShippingAddressId;
    const previousCommission = this.commission;
    const previousOrderNumber = this.orderNumber;
    const previousDeliveryDate = this.desiredDeliveryDate;

    const today = getTodayDateString();

    this.items = [];
    this.selectedShippingAddressId = null;
    this.commission = '';
    this.orderNumber = '';
    this.desiredDeliveryDate = today;
    this.notifyChange();

    try {
      await this.syncToApi();
      // Enforce the cleared state locally even if the API response does not
      // echo the sent empty commission / order number back, or does not clear
      // the delivery date. This guarantees the next order starts clean.
      this.commission = '';
      this.orderNumber = '';
      this.desiredDeliveryDate = today;
      this.notifyChange();
    } catch (error) {
      this.items = previousItems;
      this.selectedShippingAddressId = previousAddressId;
      this.commission = previousCommission;
      this.orderNumber = previousOrderNumber;
      this.desiredDeliveryDate = previousDeliveryDate;
      this.notifyChange();
      throw error;
    }
  }

  /**
   * Reset the local state (e.g. on logout).
   * Does NOT call the API.
   */
  reset(): void {
    this.items = [];
    this.commission = '';
    this.orderNumber = '';
    this.desiredDeliveryDate = '';
    this.customerId = '';
    this.cartId = null;
    this.selectedShippingAddressId = null;
    this.notifyChange();
  }

  // -- subscriptions --------------------------------------------------------

  /**
   * Register a callback that is invoked whenever the cart changes.
   *
   * @returns An unsubscribe function.
   */
  onCartChanged(callback: CartChangedCallback): () => void {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter((cb) => cb !== callback);
    };
  }

  // -- internal -------------------------------------------------------------

  /** Apply an API response to the local state. */
  private applyApiResponse(response: CartApiResponse): void {
    this.cartId = response.id;
    this.commission = response.commission ?? '';
    this.orderNumber = response.customerOrderId ?? '';
    this.desiredDeliveryDate = response.reqDeliveryDate ?? '';
    this.selectedShippingAddressId = response.selectedShippingAddressId ?? null;
    this.items = response.items.map((apiItem) => fromApiItem(apiItem, response.updatedAt));
  }

  /** Sync the current local state to the API via PUT. */
  private async syncToApi(): Promise<void> {
    if (!this.cartId) return;

    const body: CartApiRequest = {
      customerId: this.customerId,
      status: 'active',
      currency: 'EUR',
      items: this.items.map(toApiItem),
      // Send explicit empty strings (not undefined) so the API clears the
      // fields instead of leaving the previous value untouched.
      commission: this.commission,
      customerOrderId: this.orderNumber,
      reqDeliveryDate: this.desiredDeliveryDate,
      selectedShippingAddressId: this.selectedShippingAddressId ?? null,
      subtotal: 0,
      taxTotal: 0,
      grandTotal: 0,
    };

    const response = await cartApiClient.put<CartApiResponse>(
      `/api/cart/${encodeURIComponent(this.cartId)}`,
      body,
    );

    // Update local state with server response (e.g. computed totals)
    this.applyApiResponse(response);
  }

  /** Notify all local listeners and emit the global `cart-changed` event. */
  private notifyChange(): void {
    const snapshot = this.getItems();

    // Local listeners
    for (const cb of this.listeners) {
      cb(snapshot);
    }

    // Global event bus
    eventBus.emit('cart-changed', { itemCount: this.items.length });
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

/** Global CartService instance shared across the application. */
export const cartService = new CartService();
